import { useRef, useCallback } from 'react';

import {
  useBraintreeExecuteMutation,
  BraintreeExecuteRequest,
  BraintreeExecuteResponse,
} from '@/api/payments/braintreeExecute';
import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { useClient } from '@/services/client/useClient';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';

import { separatePaymentDataName } from './googlepayUtils';
import { usePaymentsConfig } from '@/services/paymentsConfig/usePaymentsConfig';

const buildExecutePayload = (
  paymentSelectState: PaymentSelectState,
  client: string,
  paymentData: google.payments.api.PaymentData,
  nonce: string,
  transactionId: string,
  accountIdentifier: string,
): BraintreeExecuteRequest => ({
  client,
  nonce,
  transactionId,
  account_identifier: accountIdentifier,
  amount: paymentSelectState.amount,
  currency: paymentSelectState.currency,
  firstName: separatePaymentDataName(paymentData?.shippingAddress?.name ?? '').firstName,
  lastName: separatePaymentDataName(paymentData?.shippingAddress?.name ?? '').lastName,
  email: paymentData?.email ?? '',
  paymentType: 'GOOGLEPAY',
});

export function useBraintreeExecute() {
  const paymentSelectState = useAppSelector(state => state.paymentSelect);

  const { getProviderGooglePayAccountIdentifier } = usePaymentsConfig();

  const googlePayAccountIdentifier = getProviderGooglePayAccountIdentifier();

  const [braintreeExecuteTrigger] = useBraintreeExecuteMutation();

  const { getClientId } = useClient();

  const onSuccess = useRef<((data: BraintreeExecuteResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);

  // Trigger payment-service execute
  const triggerExecuteApi = useCallback(async (
    paymentData: google.payments.api.PaymentData,
    nonce: string,
    transactionId: string,
    callbacks?: {
      onSuccess?: (data: BraintreeExecuteResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    onSuccess.current = callbacks?.onSuccess;
    onError.current = callbacks?.onError;

    if (!nonce) {
      logger.error('Braintree nonce not set');
      if (onError.current) {
        onError.current(new Error('Braintree nonce not set'));
      }
      return null;
    }

    // Require braintree googlepay provider config
    if (!googlePayAccountIdentifier) {
      logger.error('Error accessing Braintree GooglePay config');
      if (onError.current) {
        onError.current(new Error('Error accessing Braintree GooglePay config'));
      }
      throw new Error('Error accessing Braintree GooglePay config');
    }

    let result;
    try {
      const payload = buildExecutePayload(
        paymentSelectState,
        getClientId(),
        paymentData,
        nonce,
        transactionId,
        googlePayAccountIdentifier,
      );
      result = await braintreeExecuteTrigger(payload);
    } catch (err) {
      logger.error('Error executing Braintree transaction');
      logger.error(err);
      if (onError.current) {
        onError.current(err);
      }
    }

    // Handle payment-service execute errors
    if (!!result?.error || result?.data?.message !== 'success') {
      logger.error('Error executing Braintree transaction');
      logger.error(result?.error);
      if (onError.current) {
        onError.current(result?.error);
      }
      return null;
    }

    // Handle payment-service execute complete
    if (onSuccess.current) {
      onSuccess.current(result?.data);
    }

    return result?.data;
  }, [braintreeExecuteTrigger, getClientId, googlePayAccountIdentifier, paymentSelectState]);

  return {
    triggerExecuteApi
  };
}
